import _ from 'lodash';
import { $apply, $each, $pick, update } from 'qim';

import z from 'app/common/z';
import Errors, { resourceNames } from 'app/errors';
import { addTruthyParamsToUrl } from 'app/common/utils/UrlUtils';
import {
  MASS_REPLAY_DOWN,
  PAYMENT_ISSUE,
  PLAN_LIMIT,
} from 'app/history/utils/constants';
import {
  fetchJson,
  getJsonWithAccountId,
  postJsonWithAccountId,
} from 'app/common/fetch';

const addTimeToDateFilterParams = (queryParams, timezone) => {
  const applier = timeString => dateString => {
    const naiveDatetime = `${dateString}T${timeString}`;

    // The backend will parse the naive datetime using the profile timezone, but
    // if there's none, default to UTC while we default to the browser's. To
    // make sure results match, we send a aware UTC string in that case.
    if (!timezone) {
      return new Date(naiveDatetime).toISOString();
    }

    return naiveDatetime;
  };

  // the server will parse these from the profile's timezone to UTC
  return update(
    [
      [$pick('start_datetime'), $each, $apply(applier('00:00:00.000'))],
      [$pick('end_datetime'), $each, $apply(applier('23:59:59.999'))],
    ],
    queryParams
  );
};

const Data = {
  tasks(accountId, timezone, queryParams) {
    return getJsonWithAccountId(
      accountId,
      addTruthyParamsToUrl(
        `${z.cfg.api}/stream`,
        addTimeToDateFilterParams(queryParams, timezone)
      )
    ).catch(errorResponse => {
      throw new Errors.DataGetError({
        resource: resourceNames.tasks,
        response: errorResponse,
      });
    });
  },

  sendCSV(accountId, timezone, queryParams) {
    return postJsonWithAccountId(
      accountId,
      `${z.cfg.api}/stream/send`,
      addTimeToDateFilterParams(queryParams, timezone)
    );
  },

  massAction(action, accountId, timezone, queryParams) {
    return postJsonWithAccountId(
      accountId,
      `${z.cfg.api}/stream/${action}`,
      addTimeToDateFilterParams(queryParams, timezone)
    ).catch(response => {
      const json = JSON.parse(response.responseText);

      if (response.status === 400) {
        // TODO: We might want to change the backend to normalize its error responses.
        // In the meantime, here's the explanation:

        // Special case: if we get an array of errors, that means the server
        // didn't replay for some global reason, like too many tasks, etc.
        // The server generates a list of error messages like this:
        //
        //    [ 'Please contact support to perform an action on more that 500 tasks at a time.']
        //
        // We need to display those messages as is.
        if (_.isArray(json.errors)) {
          return {
            globalFailures: json.errors,
            globalFailureType: MASS_REPLAY_DOWN,
            channel: json.channel,
          };
        }

        // OK, normal case:
        // json.errors is an object of failure status codes and counts, like:
        //
        //   { error: 2, delayed: 4 }
        //
        // This means the server found issues with all of the tasks individually,
        // preventing them from being replayed. In this case it returns 400 instead of 202,
        // but with the same response body format as 202. 400 in this case tels us there is
        // no need to poll, since none started replaying.
        //
        // TODO: An alternate solution would be to just have the backend return 202, and the frontend
        // could deduce that if *all* the tasks were rejected initially, there's no need to poll.
        return { errors: json.errors, channel: json.channel };
      }
      if (response.status === 403) {
        return {
          globalFailures: json.errors,
          globalFailureType: json[PAYMENT_ISSUE] ? PAYMENT_ISSUE : PLAN_LIMIT,
          channel: json.channel,
        };
      }
      if (response.status === 503) {
        // 503 always means that the replay system has been turned off, due to issues.
        return {
          globalFailures: [
            'We’re having trouble with our replay system, so we held your Tasks. Visit our status page for more details. ',
          ],
          isMassActionsDisabled: true,
        };
      }

      // This would be a 500 error, unexpected, so just throw and let the user see the error box:
      throw new Errors.DataPostError({
        resource: resourceNames.taskReplay,
        response,
      });
    });
  },

  task(taskId) {
    return fetchJson(`${z.cfg.api}/stream/${taskId}`)
      .then(async task => {
        if (task.child_forks) {
          const stream = await fetchJson(
            `${z.cfg.api}/stream/${taskId}/children`
          );
          const objects = stream.objects;
          const child_task_map = _.reduce(
            objects,
            (taskMap, childTask) => {
              taskMap[childTask.fork] = childTask;
              return taskMap;
            },
            {}
          );
          return {
            ...task,
            child_task_map,
          };
        } else {
          return task;
        }
      })
      .catch(errorResponse => {
        throw new Errors.DataGetError({
          resource: resourceNames.task,
          response: errorResponse,
        });
      });
  },

  replayTask(accountId, taskId) {
    return postJsonWithAccountId(
      accountId,
      `${z.cfg.api}/stream/${taskId}/replay-task`
    ).catch(errorResponse => {
      throw new Errors.DataPostError({
        resource: resourceNames.taskReplay,
        response: errorResponse,
      });
    });
  },
};

export default Data;
